// react
import { useContext } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextDashboard } from '../Dashboard.context';

// mui
import { Box, Grid, Paper } from '@mui/material';

// ui
import { Empty } from '../../../ui/Empty/Empty';

// charts
import { ChartLine } from '../../../charts/ChartLine/ChartLine';
import { TitleBar } from '../../../ui/TitleBar/TitleBar';
import { theme } from '../../../../theme';

// props
interface SeriesProps {
	type: 'accounts' | 'contracts' | 'entities' | 'loans' | 'projects' | 'transactions' | 'users';
}

export const Series = ({ type }: SeriesProps) => {
	// context
	const { isLoadingResource, statsResource } = useContext(ContextDashboard);

	// hooks
	const { t } = useTranslation();

	// vars
	const empty = {
		content: {
			children: t('empty.default.content'),
		},
		title: {
			children: t('empty.default.title'),
		},
	};

	switch (type) {
		case 'accounts':
			empty.content = { children: t('empty.dashboard.accounts.series.content') };
			empty.title = { children: t('empty.dashboard.accounts.series.title') };
			break;
		case 'contracts':
			empty.content = { children: t('empty.dashboard.contracts.series.content') };
			empty.title = { children: t('empty.dashboard.contracts.series.title') };
			break;
		case 'entities':
			empty.content = { children: t('empty.dashboard.entities.series.content') };
			empty.title = { children: t('empty.dashboard.entities.series.title') };
			break;
		case 'loans':
			empty.content = { children: t('empty.dashboard.loans.series.content') };
			empty.title = { children: t('empty.dashboard.loans.series.title') };
			break;
		case 'projects':
			empty.content = { children: t('empty.dashboard.projects.series.content') };
			empty.title = { children: t('empty.dashboard.projects.series.title') };
			break;
		case 'transactions':
			empty.content = { children: t('empty.dashboard.transactions.series.content') };
			empty.title = { children: t('empty.dashboard.transactions.series.title') };
			break;
		case 'users':
			empty.content = { children: t('empty.dashboard.users.series.content') };
			empty.title = { children: t('empty.dashboard.users.series.title') };
			break;
	}

	let content = (
		<Empty
			content={empty.content}
			hasContent={!isLoadingResource}
			hasTitle={!isLoadingResource}
			isLoading={isLoadingResource}
			position="absolute"
			title={empty.title}
		/>
	);

	if (statsResource) {
		const resource = statsResource[type];
		if (resource?.series && Array.isArray(resource?.series) && resource?.series?.length > 0) {
			content = <ChartLine data={resource.series} />;
		}
	}

	return (
		<Paper sx={{ height: '100%' }}>
			<Grid container direction="column" sx={{ height: '100%' }}>
				<Grid item>
					<TitleBar
						BoxProps={{ sx: { borderTopLeftRadius: 8, borderTopRightRadius: 8 } }}
						title={{ children: t('series') }}
					/>
				</Grid>
				<Grid flex={1} item>
					<Box sx={{ position: 'relative', padding: theme.spacing(2, 2, 2, 0), height: '100%' }}>
						{content}
					</Box>
				</Grid>
			</Grid>
		</Paper>
	);
};
